import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  Package, Building2, Loader2, Grid3X3, LayoutList, Search,
  ChevronDown, ChevronUp, Warehouse, Pencil, X, Save, Download, Calendar
} from 'lucide-react';

const StockStatus = () => {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [offices, setOffices] = useState([]);
  const [inventoryByProduct, setInventoryByProduct] = useState([]);
  const [inventoryByOffice, setInventoryByOffice] = useState([]);
  const [totals, setTotals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOffices, setExpandedOffices] = useState({});
  
  // Date filter for export
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportDate, setExportDate] = useState(new Date().toISOString().split('T')[0]);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Edit modal state
  const [editModal, setEditModal] = useState(null); // { productId, productName, productSku, officeId, officeName, currentQty }
  const [editQty, setEditQty] = useState('');
  const [saving, setSaving] = useState(false);

  // Check if current user is admin
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [officesRes, byProductRes, byOfficeRes, totalsRes] = await Promise.all([
        api.getStockOffices(),
        api.getInventoryByProduct(),
        api.getInventoryByOffice(),
        api.getInventoryTotals()
      ]);
      setOffices(officesRes.data);
      setInventoryByProduct(byProductRes.data);
      setInventoryByOffice(byOfficeRes.data);
      setTotals(totalsRes.data);
      
      // Expand all offices by default
      const expanded = {};
      byOfficeRes.data.forEach(office => {
        expanded[office.office_id] = true;
      });
      setExpandedOffices(expanded);
    } catch (error) {
      toast.error('Failed to fetch stock data');
    } finally {
      setLoading(false);
    }
  };

  const toggleOffice = (officeId) => {
    setExpandedOffices(prev => ({
      ...prev,
      [officeId]: !prev[officeId]
    }));
  };

  const openEditModal = (productId, productName, productSku, officeId, officeName, currentQty) => {
    setEditModal({ productId, productName, productSku, officeId, officeName, currentQty });
    setEditQty(currentQty.toString());
  };

  const closeEditModal = () => {
    setEditModal(null);
    setEditQty('');
  };

  const handleSaveStock = async () => {
    if (!editModal) return;
    
    const newQty = parseInt(editQty);
    if (isNaN(newQty) || newQty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setSaving(true);
    try {
      await api.adjustInventory({
        product_id: editModal.productId,
        office_id: editModal.officeId,
        quantity: newQty,
        reason: `Manual adjustment from ${editModal.currentQty} to ${newQty}`
      });
      toast.success(`Stock updated: ${editModal.productSku} at ${editModal.officeName} → ${newQty}`);
      closeEditModal();
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    } finally {
      setSaving(false);
    }
  };

  const exportToCSV = async () => {
    setExportLoading(true);
    
    try {
      // Fetch inventory data for the specific date
      const response = await api.getInventoryByDate(exportDate);
      const dateData = response.data;
      
      if (!dateData.products || dateData.products.length === 0) {
        toast.error(`No stock data found for ${exportDate}`);
        setExportLoading(false);
        return;
      }
      
      // Use offices from the response
      const exportOffices = dateData.offices || offices;
      
      // Prepare CSV data
      const headers = ['SKU', 'Product Name', ...exportOffices.map(o => o.name), 'Total'];
      
      const rows = dateData.products.map(item => {
        const row = [
          item.product_sku,
          item.product_name,
          ...exportOffices.map(office => {
            const stockData = item.stock_by_office[office.id];
            return stockData?.quantity || 0;
          }),
          item.total_stock
        ];
        return row;
      });

      // Create CSV content
      const csvContent = [
        `Stock Status Report - Date: ${exportDate}`,
        '',
        headers.join(','),
        ...rows.map(row => row.map(cell => 
          typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
        ).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `stock_status_${exportDate}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Stock data exported for ${exportDate}`);
      setShowExportPanel(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export stock data');
    } finally {
      setExportLoading(false);
    }
  };

  const filteredByProduct = inventoryByProduct.filter(item =>
    item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.product_sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTotals = totals.filter(item =>
    item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.product_sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="stock-status" className="max-w-[1600px] mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
            Stock Status
          </h1>
          <p className="text-slate-600 font-body">View current stock levels across all warehouses.</p>
        </div>

        {/* View Toggle & Search */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 w-64"
            />
          </div>
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm' : 'hover:bg-slate-200'}`}
              title="Table View"
            >
              <LayoutList size={20} />
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'card' ? 'bg-white shadow-sm' : 'hover:bg-slate-200'}`}
              title="Card View"
            >
              <Grid3X3 size={20} />
            </button>
          </div>
          <button
            onClick={() => setShowExportPanel(!showExportPanel)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              showExportPanel 
                ? 'bg-green-700 text-white' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            title="Export to CSV"
          >
            <Calendar size={18} />
            Export Stock
          </button>
        </div>
      </div>

      {/* Export Panel with Date Selection */}
      {showExportPanel && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Export Stock Report</h3>
                <p className="text-sm text-slate-600">Select date and download stock status as CSV</p>
              </div>
            </div>
            <button 
              onClick={() => setShowExportPanel(false)}
              className="p-1 hover:bg-green-100 rounded-lg text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Report Date</label>
              <input
                type="date"
                value={exportDate}
                onChange={(e) => setExportDate(e.target.value)}
                className="px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white min-w-[200px]"
              />
            </div>
            <button
              onClick={exportToCSV}
              disabled={exportLoading}
              className="px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-medium flex items-center gap-2 transition-colors shadow-sm"
            >
              {exportLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download CSV
                </>
              )}
            </button>
          </div>
          
          <p className="text-xs text-green-700 mt-4">
            📊 Export will include {filteredByProduct.length} products across {offices.length} warehouses
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total SKUs</p>
          <p className="text-2xl font-bold text-slate-900">{inventoryByProduct.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Warehouses</p>
          <p className="text-2xl font-bold text-slate-900">{offices.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Items</p>
          <p className="text-2xl font-bold text-cyan-600">
            {inventoryByProduct.reduce((sum, item) => sum + item.total_stock, 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">SKUs with Stock</p>
          <p className="text-2xl font-bold text-green-600">
            {inventoryByProduct.filter(item => item.total_stock > 0).length}
          </p>
        </div>
      </div>

      {viewMode === 'table' ? (
        /* Table View */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">SKU</th>
                  <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">Product Name</th>
                  {offices.map(office => (
                    <th key={office.id} className="text-center px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                      {office.name}
                    </th>
                  ))}
                  <th className="text-center px-6 py-4 text-xs font-bold uppercase tracking-wider text-cyan-600 bg-cyan-50">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredByProduct.length === 0 ? (
                  <tr>
                    <td colSpan={offices.length + 3} className="text-center py-12 text-slate-500">
                      <Package size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No products found</p>
                    </td>
                  </tr>
                ) : (
                  filteredByProduct.map(item => (
                    <tr key={item.product_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{item.product_sku}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-slate-900">{item.product_name}</span>
                      </td>
                      {offices.map(office => {
                        const stockData = item.stock_by_office[office.id];
                        const quantity = stockData?.quantity || 0;
                        return (
                          <td key={office.id} className="px-6 py-4 text-center">
                            <div className="group inline-flex items-center gap-1">
                              <span className={`font-medium ${quantity > 0 ? 'text-slate-900' : 'text-slate-300'}`}>
                                {quantity.toLocaleString()}
                              </span>
                              {isAdmin && (
                                <button
                                  onClick={() => openEditModal(item.product_id, item.product_name, item.product_sku, office.id, office.name, quantity)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                  title="Edit stock"
                                >
                                  <Pencil size={12} />
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 text-center bg-cyan-50">
                        <span className="font-bold text-cyan-700">{item.total_stock.toLocaleString()}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Card View */
        <div className="space-y-6">
          {inventoryByOffice.map(office => (
            <div key={office.office_id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => toggleOffice(office.office_id)}
                className="w-full px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Warehouse size={24} className="text-cyan-600" />
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-slate-900">{office.office_name}</h3>
                    <p className="text-sm text-slate-500">
                      {office.total_skus} SKUs • {office.total_items.toLocaleString()} items
                    </p>
                  </div>
                </div>
                {expandedOffices[office.office_id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              
              {expandedOffices[office.office_id] && (
                <div className="p-4">
                  {office.products.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No stock at this location</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {office.products
                        .filter(product => 
                          product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.product_sku.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map(product => (
                          <div
                            key={product.product_id}
                            className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-cyan-300 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-xs text-slate-500">{product.product_sku}</p>
                                <p className="font-medium text-slate-900 truncate">{product.product_name}</p>
                              </div>
                              <div className="ml-3 text-right">
                                <p className="text-xl font-bold text-cyan-600">{product.quantity.toLocaleString()}</p>
                                <p className="text-xs text-slate-500">{product.unit}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Stock Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Edit Stock Quantity</h3>
              <button onClick={closeEditModal} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-slate-500">Product</p>
                <p className="font-medium text-slate-900">{editModal.productSku} - {editModal.productName}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Warehouse</p>
                <p className="font-medium text-slate-900">{editModal.officeName}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Current Quantity</p>
                <p className="font-medium text-slate-900">{editModal.currentQty.toLocaleString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Quantity</label>
                <input
                  type="number"
                  min="0"
                  value={editQty}
                  onChange={(e) => setEditQty(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-lg font-medium"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStock}
                disabled={saving}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockStatus;
